import { Backup, BackupViewResponse, DeductionDetailJson, DynamicBackupTable } from "@/api"
import { BackupTable } from "@/deduction/[id]/BackupTable.tsx"
import { backup_columns } from "@/deduction/[id]/backup_columns.tsx"
import { FileViewer } from "@/accounting/file_viewer.tsx"
import { Download, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { convertBackupToCsv, downloadCsv } from "@/deduction_table/util"
import { useMemo, useState } from "preact/hooks"
import { Ref } from "preact"
import { ColumnHeader } from "@/deduction_table/column_header.tsx"
import { TableColumn } from "@/deduction_table/columns.tsx"
import {
  backup_deduction_amount_diff,
  display_backup_total_for_deduction,
  exactCurrencyFormatter,
  format_transaction_amount,
  rateFormatter,
  wakefern_backup_match_status,
} from "@/utils"

interface BackupSectionProps {
  deduction: DeductionDetailJson
  backupResponse: BackupViewResponse
  backupTableRef: Ref<HTMLDivElement>
}

function downloadParsedBackup(backup: Backup[], deduction_id: string) {
  const plainData: Record<string, number | string | boolean>[] = backup.map((b: Backup) => {
    const plain: Record<string, number | string | boolean> = {}
    for (const [key, value] of Object.entries(b as unknown as Record<string, unknown>)) {
      if (value === null || value === undefined) {
        plain[key] = ""
      } else if (value instanceof Date) {
        plain[key] = value.toISOString()
      } else if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
        plain[key] = value
      } else {
        plain[key] = String(value)
      }
    }
    return plain
  })
  const csv = convertBackupToCsv(plainData)
  downloadCsv(csv, `parsed_backup_${deduction_id}`)
}

function downloadDynamicBackup(table: DynamicBackupTable, deduction_id: string) {
  downloadCsv(convertBackupToCsv(table.rows), `parsed_backup_${deduction_id}`)
}

export function buildDynamicBackupColumns(table: DynamicBackupTable | null | undefined): TableColumn<Record<string, string>>[] {
  if (!table) return []
  return table.columns.map(column => ({
    accessorKey: column.id,
    header: ({ column: tableColumn }) => <ColumnHeader column={tableColumn} title={column.label} />,
  }))
}

interface WakefernBackupSummaryProps {
  backup: Backup[]
  invoiceAmount?: number
}

const FEE_ROW_DESCRIPTIONS = new Set(["Post Damage Costs", "Reclamation Center Costs"])

function MatchBadge({ matchStatus, diff }: { matchStatus: string | null; diff: number | null }) {
  if (matchStatus === "match") {
    return (
      <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        ✓ Matches
      </span>
    )
  }
  if (matchStatus === "close") {
    return (
      <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        ≈ Off by {exactCurrencyFormatter(diff!)}
      </span>
    )
  }
  if (matchStatus === "mismatch") {
    return (
      <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        ✗ Differs by {exactCurrencyFormatter(diff!)}
      </span>
    )
  }
  return null
}

export function WakefernBackupSummary({ backup, invoiceAmount }: WakefernBackupSummaryProps) {
  const rows = backup.filter((b: Backup) => b.source === "wakefern")
  if (rows.length === 0) return null

  // Exclude summary fee rows (virtual rows appended by the server) from unit/admin totals
  const productRows = rows.filter((b: Backup) => !FEE_ROW_DESCRIPTIONS.has(b.product_description ?? ""))
  const feeRows = rows.filter((b: Backup) => FEE_ROW_DESCRIPTIONS.has(b.product_description ?? ""))

  const totalQuantity = productRows.reduce((sum: number, b: Backup) => sum + (b.quantity ?? 0), 0)
  const backupTotal = rows.reduce((sum: number, b: Backup) => sum + (b.total ?? 0), 0)
  const productTotal = productRows.reduce((sum: number, b: Backup) => sum + (b.total ?? 0), 0)

  const diff = invoiceAmount != null ? backup_deduction_amount_diff(backupTotal, invoiceAmount) : null
  const matchStatus = wakefern_backup_match_status(backupTotal, invoiceAmount)
  const displayBackupTotal = display_backup_total_for_deduction(backupTotal)

  const isReclamation = productRows.some((b: Backup) => b.promo_type === "reclamation")

  if (isReclamation) {
    // Derive per-unit post-damage rate from admin_fee / quantity for the first row that has both
    const firstRateRow = productRows.find(
      (b: Backup) => b.admin_fee != null && b.quantity != null && b.quantity > 0
    )
    const perUnitPostDamage = firstRateRow != null ? firstRateRow.admin_fee! / firstRateRow.quantity! : null

    // Use uniform unit_amount if all product rows share the same value
    const unitAmountValues = new Set(productRows.map((b: Backup) => b.unit_amount))
    const uniformUnitAmount = unitAmountValues.size === 1 ? productRows[0]?.unit_amount : null

    const postDamageFee = feeRows.find((b: Backup) => b.product_description === "Post Damage Costs")
    const reclamCenterFee = feeRows.find((b: Backup) => b.product_description === "Reclamation Center Costs")

    return (
      <div class="px-4 py-3 mb-3 bg-slate-50 border border-slate-200 rounded-lg text-sm space-y-1">
        <div class="flex flex-wrap items-baseline gap-1 text-slate-700">
          <span class="font-semibold text-slate-900">{totalQuantity.toLocaleString()} units</span>
          <span class="text-slate-400">×</span>
          <span class="font-mono">
            ({uniformUnitAmount != null ? rateFormatter(uniformUnitAmount) : "various"}
            {perUnitPostDamage != null ? ` + ${rateFormatter(perUnitPostDamage)}` : ""})
          </span>
          <span class="text-slate-400">=</span>
          <span class="font-semibold">{exactCurrencyFormatter(productTotal)}</span>
        </div>
        {postDamageFee != null && (
          <div class="flex items-baseline gap-1 text-slate-600 pl-2">
            <span>+ Post Damage Costs:</span>
            <span class="font-medium text-slate-800">{exactCurrencyFormatter(postDamageFee.total ?? 0)}</span>
          </div>
        )}
        {reclamCenterFee != null && (
          <div class="flex items-baseline gap-1 text-slate-600 pl-2">
            <span>+ Reclamation Center Costs:</span>
            <span class="font-medium text-slate-800">{exactCurrencyFormatter(reclamCenterFee.total ?? 0)}</span>
          </div>
        )}
        <div class="flex flex-wrap items-center gap-3 pt-1.5 mt-1 border-t border-slate-200">
          <div class="flex items-baseline gap-1">
            <span class="text-slate-500 text-xs">=</span>
            <span class="text-lg font-bold text-slate-900">{exactCurrencyFormatter(displayBackupTotal)}</span>
          </div>
          {invoiceAmount != null && (
            <>
              <div class="flex items-baseline gap-1">
                <span class="text-xs text-slate-500">Deduction:</span>
                <span class="font-semibold text-slate-800">{format_transaction_amount("deduction", invoiceAmount)}</span>
              </div>
              <MatchBadge matchStatus={matchStatus} diff={diff} />
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div class="flex flex-wrap items-center gap-4 px-4 py-3 mb-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
      <div class="flex flex-col">
        <span class="text-xs text-slate-500 font-medium uppercase tracking-wide">Total Units</span>
        <span class="text-2xl font-bold text-slate-900 leading-tight">{totalQuantity.toLocaleString()}</span>
      </div>
      <div class="w-px h-10 bg-slate-200 hidden sm:block" />
      <div class="flex flex-col">
        <span class="text-xs text-slate-500 font-medium uppercase tracking-wide">Backup Total</span>
        <span class="text-lg font-semibold text-slate-800">{exactCurrencyFormatter(displayBackupTotal)}</span>
      </div>
      {invoiceAmount != null && (
        <>
          <div class="w-px h-10 bg-slate-200 hidden sm:block" />
          <div class="flex flex-col">
            <span class="text-xs text-slate-500 font-medium uppercase tracking-wide">Deduction Amount</span>
            <span class="text-lg font-semibold text-slate-800">{format_transaction_amount("deduction", invoiceAmount)}</span>
          </div>
          <div class="flex flex-col justify-center">
            <MatchBadge matchStatus={matchStatus} diff={diff} />
          </div>
        </>
      )}
    </div>
  )
}

async function downloadExcel(deduction_id: string) {
  const response = await fetch(`/api/deduction/${deduction_id}/export-excel`, {
    credentials: "include",
  })
  if (!response.ok) {
    throw new Error("Export failed")
  }
  const blob = await response.blob()
  const contentDisposition = response.headers.get("Content-Disposition")
  let filename = `invoice_${deduction_id}.xlsx`
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/)
    if (match) {
      filename = match[1]
    }
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function BackupSection({ deduction, backupResponse, backupTableRef }: BackupSectionProps) {
  // Backup lines are in the deduction's own currency.
  const backup_meta = useMemo(() => ({ currency: deduction.currency }), [deduction.currency])
  const [excelLoading, setExcelLoading] = useState(false)
  const backup = backupResponse.rows
  const dynamicTable = backupResponse.dynamic_table
  const dynamicColumns = useMemo(() => buildDynamicBackupColumns(dynamicTable), [dynamicTable])
  const showDynamicTable = !!dynamicTable && dynamicTable.rows.length > 0 && dynamicColumns.length > 0

  async function handleDownloadExcel() {
    if (excelLoading) return
    setExcelLoading(true)
    try {
      await downloadExcel(deduction.id)
    } finally {
      setExcelLoading(false)
    }
  }

  return (
    <>
      {backup.length > 0 && (
        <div ref={backupTableRef} class="w-full min-h-44" id="backup-table-section">
          {showDynamicTable ? (
            <BackupTable<Record<string, string>, unknown>
              columns={dynamicColumns}
              data={dynamicTable!.rows}
              toolbar={
                <div class="flex gap-2">
                  <Button variant="outline" class="text-slate-500" disabled={excelLoading} onClick={handleDownloadExcel}>
                    {excelLoading ? <Loader2 className="h-4 mr-2 animate-spin" /> : <Download className="h-4 mr-2" />}
                    Download Excel
                  </Button>
                  <Button variant="outline" class="text-slate-500" onClick={() => downloadDynamicBackup(dynamicTable!, deduction.id)}>
                    <Download className="h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              }
            />
          ) : (
            <>
              <WakefernBackupSummary backup={backup} invoiceAmount={deduction.invoice_amount} />
              <BackupTable<Backup, unknown>
                columns={backup_columns}
                data={backup}
                meta={backup_meta}
                toolbar={
                  <div class="flex gap-2">
                    <Button variant="outline" class="text-slate-500" disabled={excelLoading} onClick={handleDownloadExcel}>
                      {excelLoading ? <Loader2 className="h-4 mr-2 animate-spin" /> : <Download className="h-4 mr-2" />}
                      Download Excel
                    </Button>
                    <Button variant="outline" class="text-slate-500" onClick={() => downloadParsedBackup(backup, deduction.id)}>
                      <Download className="h-4 mr-2" />
                      Download CSV
                    </Button>
                  </div>
                }
              />
            </>
          )}
        </div>
      )}
      {deduction.backup_s3_uri?.toLowerCase().endsWith(".pdf") && (
        <div class="mt-6 mx-4 mb-8">
          <h3 class="text-lg font-semibold mb-4">Backup Document</h3>
          <div class="h-[calc(100vh-50px)] border border-gray-200 rounded-lg overflow-hidden">
            <FileViewer s3Uri={deduction.backup_s3_uri} fileName={`${deduction.customer_name} Backup`} />
          </div>
        </div>
      )}
    </>
  )
}
